// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";
import { useNavigate } from "react-router-dom";

// material styles
import {
  Grid,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// request methods
import {
  addServiceRequest,
  getServiceSubCategoryRequest,
} from "api/requests/services";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import UploadVideoCM from "components/shared/Video/Upload";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";

// helper
import Toast from "helper/toast";

// form validate
import { pageItemSchema, validateImg } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./addServices.style";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import BackButton from "components/shared/BackButton";

// services categories...
const categoryList = [
  { value: "consultation", title: "مشاوره" },
  { value: "performance", title: "اجرا" },
  { value: "supervision", title: "نظارت" },
  { value: "export-import-clearance", title: "صادرات واردات و ترخیص" },
];

export default function AddService() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(pageItemSchema),
  });

  // editor value
  const [text, setText] = useState("");

  // checkbox state
  const [checked, setChecked] = useState(false);

  const handleCheckChange = () => setChecked((state) => !state);

  // upload image and video state
  const [serviceImage, setServiceImage] = useState({
    url: null,
    isChanged: false,
  });
  const [serviceVideo, setServiceVideo] = useState({
    url: null,
    isChanged: false,
  });

  // text editor value handler method
  const handleChangeText = (text) => setText(text);

  // select option state
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const [allSubCategories, setAllSubCategories] = useState({
    data: [],
    count: 0,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);

  // add service
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!serviceImage.url) return Toast({ type: "empty", title: "فیلد عکس " });
    const res = await request({
      url: () =>
        addServiceRequest(
          {
            title: data.title,
            subcategory_id: selectedSubCategory,
            text,
            alt: data.alt,
            meta_description: data.meta_description,
            keyword: data.keyword,
            show_in_home_page: checked ? 1 : 0,
            main_image: serviceImage,
            video: serviceVideo,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    if (res) {
      Toast({ type: "success", title: "افزودن خدمات" });
      navigate("/services");
    }
  };

  // uploaded image handler method
  const handleChangeServicesImage = (val) => {
    validateImg(val);
    setServiceImage({ url: val, isChanged: true });
  };

  // uploaded video handler method
  const handleChangeServicesVideo = (val) =>
    setServiceVideo({ url: val, isChanged: true });

  // get subcategory
  const getServiceSubCategoryData = useCallback(async () => {
    const res = await request({
      url: getServiceSubCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });
    if (res) {
      setAllSubCategories({ data: res.data.data, count: res.data.count });
    }
  }, [request]);

  useEffect(() => {
    setSelectedSubCategory(null);
    let data = allSubCategories.data.filter(
      (item) => item.category === selectedCategory
    );

    let transformedData = data.map((item) => ({
      id: item.id,
      title: item.title,
    }));
    setSubCategoryList(transformedData);
  }, [selectedCategory]);

  useEffect(() => {
    getServiceSubCategoryData();
  }, [getServiceSubCategoryData]);

  return (
    <DrawerCM title="افزودن خدمات">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <Grid item mt={3} xs={12}>
            <BackButton Route="/services" />
          </Grid>
          <StyledStack>
            <StyledTitle>افزودن خدمات</StyledTitle>
            <Grid container>
              <Grid item md={4} xs={12} p={1.2}>
                <TextField
                  label="عنوان خدمات"
                  variant="outlined"
                  fullWidth
                  id="title"
                  {...register("title")}
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                />
              </Grid>

              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="سردسته"
                  control={control}
                  title="سردسته"
                  id="category"
                  fullWidth
                  {...register("category")}
                  error={Boolean(errors.category)}
                  helperText={errors.category?.message}
                  onChange={(value) => setSelectedCategory(value)}
                >
                  {categoryList?.map((item, index) => (
                    <StyledMenuItem key={index} value={item.value}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="زیردسته"
                  control={control}
                  title="زیردسته"
                  id="subCategory"
                  {...register("subCategory")}
                  error={Boolean(errors.subCategory)}
                  helperText={errors.subCategory?.message}
                  onChange={(value) => setSelectedSubCategory(value)}
                >
                  {subCategoryList?.map((item, index) => (
                    <StyledMenuItem key={index} value={item.id}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {serviceImage.url ? (
                  <Upload
                    isWeb={serviceImage.isWeb}
                    isLocal={!serviceImage.isWeb}
                    file={serviceImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeServicesImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={serviceImage.isChanged}
                    file={serviceImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeServicesImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {serviceVideo.url ? (
                  <UploadVideoCM
                    isWeb={serviceVideo.isWeb}
                    isLocal={!serviceVideo.isWeb}
                    file={serviceVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeServicesVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                ) : (
                  <UploadVideoCM
                    isLocal={serviceVideo.isChanged}
                    file={serviceVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeServicesVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label=" متن جایگزین"
                  variant="outlined"
                  fullWidth
                  id="alt"
                  {...register("alt")}
                  error={Boolean(errors.alt)}
                  helperText={errors.alt?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label="کلمه کلیدی"
                  variant="outlined"
                  fullWidth
                  id="keyword"
                  {...register("keyword")}
                  error={Boolean(errors.keyword)}
                  helperText={errors.keyword?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <TextField
                  label="فراتوضیحات"
                  variant="outlined"
                  fullWidth
                  id="meta_description"
                  {...register("meta_description")}
                  error={Boolean(errors.meta_description)}
                  helperText={errors.meta_description?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <Editor
                  enableToolbar={true}
                  onChange={handleChangeText}
                  defaultValue={text}
                  setDefaultStyle="font-family: IranSans; height: 40vh;"
                  setOptions={{
                    buttonList: buttonList.complex,
                    font: [
                      "IranSans",
                      "IranSansBold",
                      "Arial",
                      "Impact",
                      "Tahoma",
                    ],
                  }}
                />
              </Grid>

              <ButtonContainer>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
        </Grid>
      )}
    </DrawerCM>
  );
}
